import { DndContext, DragOverlay, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import { cn } from '@plato/ui/util/classes';
import { formatISO, intlFormat, parseISO } from 'date-fns';
import type { ComponentPropsWithRef, ReactNode } from 'react';
import { EventsLoadingState } from '../../events/components/events-loading-state.tsx';
import { getSortableEventId } from '../../events/utils/get-sortable-event-id.ts';
import { useWeekEventsBoard } from '../hooks/use-week-events-board.ts';
import { DaySuggestions } from './day-suggestions.tsx';
import { EmptyDay } from './empty-day.tsx';
import { NewEventButton } from './new-event-button.tsx';
import { OverlayEventCard } from './overlay-event-card.tsx';
import { SortableEventCard } from './sortable-event-card.tsx';

export type Props = {
    dateFrom: string;
    dateTo: string;
    ownerId: string;
    days: string[];
};

type DayColumnProps = ComponentPropsWithRef<'div'> & {
    day: string;
};

/** A day's column, and the drop target for cards dragged in from another day. */
function DayColumn({ day, className, ...props }: DayColumnProps): ReactNode {
    const { setNodeRef } = useDroppable({ id: day });

    return (
        <div
            ref={setNodeRef}
            data-testid={`day-column-${day}`}
            // The floor fits a card's full top row (time range, recurrence, zip); wider screens share the rest
            className={cn('flex w-[202px] shrink-0 grow flex-col bg-container pb-relaxed', className)}
            {...props}
        />
    );
}

/** Monday to Friday side by side, each day a column of event cards with the route suggestions below. */
export function EventsOverviewBodyWeek({ dateFrom, dateTo, ownerId, days }: Props): ReactNode {
    const locale = useResolvedLocale();
    const today = formatISO(new Date(), { representation: 'date' });
    // Working days only, as in the design: hardly any events are created on weekends, and the day view still reaches them
    const workDays = days.slice(0, 5);
    const { isLoading, eventsByDays, draggedEvent, dndContextProps } = useWeekEventsBoard({
        dateFrom,
        dateTo,
        ownerId,
        days: workDays,
    });

    if (isLoading) {
        return <EventsLoadingState />;
    }

    return (
        <DndContext {...dndContextProps}>
            {/* The grid scrolls both ways itself: an outer horizontal scroller would break the sticky day headers.
                It reaches the window edge, and pads back to the page's padding so the columns line up with the header. */}
            <div className="h-full w-full overflow-auto px-5 sm:px-4">
                <div className="flex min-h-full w-fit min-w-full flex-row items-stretch divide-x divide-line-weak">
                    {workDays.map((day) => {
                        const events = eventsByDays[day] ?? [];
                        const date = parseISO(day);

                        return (
                            <DayColumn key={day} day={day}>
                                <div className="sticky top-0 z-10 flex flex-col items-center border-b border-line-weak bg-inherit px-default pt-cozy pb-tight">
                                    <span className="text-caption text-content-weak">
                                        {intlFormat(date, { weekday: 'long' }, { locale })}
                                    </span>
                                    <span
                                        className={cn(
                                            'flex size-7 items-center justify-center text-body-emphasis',
                                            day === today ? 'text-brand' : 'text-content',
                                        )}
                                    >
                                        {intlFormat(date, { day: 'numeric' }, { locale })}
                                    </span>
                                </div>
                                <SortableContext
                                    id={day}
                                    items={events.map((event) => getSortableEventId(event))}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <div className="flex grow flex-col gap-cozy px-cozy pt-cozy">
                                        {events.length === 0 && (
                                            <EmptyDay day={day} ownerId={ownerId} className="pt-tight" />
                                        )}
                                        {events.map((event) => (
                                            <SortableEventCard
                                                key={event.event_id}
                                                event={event}
                                                date={event.date}
                                            />
                                        ))}
                                        {events.length > 0 && <NewEventButton day={day} ownerId={ownerId} />}
                                        {/* Suggestions are anchored on the day's visits; without any, there is no route to anchor on.
                                            Past days can no longer be planned. ISO date strings compare correctly as strings. */}
                                        {events.length > 0 && day >= today && (
                                            <div className="pt-default">
                                                <DaySuggestions day={day} ownerId={ownerId} />
                                            </div>
                                        )}
                                    </div>
                                </SortableContext>
                            </DayColumn>
                        );
                    })}
                </div>
            </div>

            <DragOverlay>
                {draggedEvent ? <OverlayEventCard event={draggedEvent} date={draggedEvent.date} /> : null}
            </DragOverlay>
        </DndContext>
    );
}
